// Shared invoice helpers — used by both FE and edge functions.
// Pure TypeScript, no runtime dependencies.

import type { PricingSide, VatRateCode } from './types.ts';

// ── Product name / description builders ─────────────────────────────────

/** Any product-ish object; fields are read defensively (callers span FE rows and raw edge payloads). */
type ProductLike = Record<string, unknown> | null | undefined;

export function resolveName(
  sideNames: Record<string, string> | undefined,
  product: unknown,
  lang: string,
): string {
  const p = product as ProductLike;
  // Values are text columns; the cast documents that without altering the
  // original `||` fall-through (any falsy value still moves to the next branch).
  const pick = (key: string): string | undefined => p?.[key] as string | undefined;
  return (
    sideNames?.[lang] ||
    sideNames?.['en'] ||
    pick(`name_${lang}`) ||
    pick('name_pl') ||
    pick('name') ||
    'Unknown Product'
  );
}

export function buildProductName(
  tx: unknown,
  language: string = 'pl',
  side: 'load' | 'unload' = 'unload',
): string {
  const product = (tx as { product?: unknown }).product as ProductLike;
  const sideNames = (side === 'load' ? product?.['invoice_names_load'] : product?.['invoice_names_unload']) as
    Record<string, string> | undefined;

  let productName: string;
  if (language.includes('/')) {
    const [, secondaryLang] = language.split('/');
    const plName = resolveName(sideNames, product, 'pl');
    const secondaryName = resolveName(sideNames, product, secondaryLang);
    productName = plName === secondaryName ? plName : `${plName} / ${secondaryName}`;
  } else {
    productName = resolveName(sideNames, product, language);
  }

  const wasteCode = product?.['waste_code'] as string | null | undefined;
  return wasteCode ? `${productName} (${wasteCode})` : productName;
}

export function buildDescription(tx: unknown, nonEuExtra?: { containerNumber?: string | null; numberOfBales?: number | null }): string {
  const row = tx as { reference_number?: string | null; external_id?: string | null };
  const lines: string[] = [];
  if (nonEuExtra?.containerNumber) lines.push(`Numer kontenera/Container nr: ${nonEuExtra.containerNumber}`);
  if (nonEuExtra?.numberOfBales) lines.push(`Ilość belek/Number of bales: ${nonEuExtra.numberOfBales}`);
  lines.push([row.reference_number, row.external_id || null].filter(Boolean).join(' - '));
  return lines.join('\n');
}

// ── VAT rate ────────────────────────────────────────────────────────────

/** Resolve a tax string to a VatRateCode. Defaults to '23'. */
export function resolveVatRateCode(taxStr: string): VatRateCode {
  const t = typeof taxStr === 'string' ? taxStr.toLowerCase().trim() : String(taxStr ?? '');
  if (t === 'zw' || t === 'np') return t;
  if (t === '0') return '0';
  if (t === '5') return '5';
  if (t === '8') return '8';
  if (t === '23') return '23';
  return '23';
}

// ── Transaction validation ──────────────────────────────────────────────

export interface TransactionValidationResult {
  isValid: boolean;
  errors: string[];
  code?: string;
}

/**
 * Validate transactions before invoice generation.
 * Works with any object that has side-dependent fields (FE TransactionWithRelations or edge tx: any).
 *
 * @param transactions - array of transaction objects
 * @param side - 'load' or 'unload'
 * @param getSideFieldsFn - function to extract side-dependent fields from a transaction
 */
export function validateTransactions<T>(
  transactions: T[],
  side: PricingSide,
  getSideFieldsFn: (tx: T, side: PricingSide) => { companyId: number | null; currency: string; price: number | null; date: string | null },
): TransactionValidationResult {
  const errors: string[] = [];

  if (transactions.length === 0) {
    return { isValid: false, errors: ['No transactions selected'], code: 'INVOICE_NO_TRANSACTIONS' };
  }

  const companyIds = [...new Set(transactions.map((tx) => getSideFieldsFn(tx, side).companyId))];
  if (companyIds.length > 1) errors.push('All transactions must be for the same company');

  const currencies = [...new Set(transactions.map((tx) => getSideFieldsFn(tx, side).currency))];
  if (currencies.length > 1) errors.push(`All transactions must have the same currency. Found: ${currencies.join(', ')}`);

  const missingPrices = transactions.filter((tx) => getSideFieldsFn(tx, side).price == null);
  if (missingPrices.length > 0) errors.push(`${missingPrices.length} transaction(s) are missing ${side} price`);

  const missingDates = transactions.filter((tx) => !getSideFieldsFn(tx, side).date);
  if (missingDates.length > 0) errors.push(`${missingDates.length} transaction(s) are missing ${side} date`);

  // Determine primary error code for the first validation failure
  let code: string | undefined;
  if (errors.length > 0) {
    if (errors.some(e => e.includes('same company'))) code = 'INVOICE_MIXED_COMPANIES';
    else if (errors.some(e => e.includes('same currency'))) code = 'INVOICE_MIXED_CURRENCIES';
    else if (errors.some(e => e.includes('missing') && e.includes('price'))) code = 'INVOICE_MISSING_PRICES';
    else if (errors.some(e => e.includes('missing') && e.includes('date'))) code = 'INVOICE_MISSING_DATES';
  }

  return { isValid: errors.length === 0, errors, ...(code ? { code } : {}) };
}
